/**
 * Identity helpers — no React, no DOM, no network.
 *
 * The token IS the source: IAM mints an `orgs` claim (the whole membership set,
 * home first, each with its coarse role), so a switcher needs no API call and
 * cannot disagree with what the resource server will authorize
 * (`X-Org-Id ∈ orgs`). Display names come from userinfo + claims, and an id is
 * never a name.
 *
 * @packageDocumentation
 */

/** The user as it should be shown: a name a human recognizes, never an id. */
export interface IamIdentity {
  /** Display name, falling back through the claims to the local part of the email. */
  name: string;
  email: string | null;
  /** Avatar URL when IAM has one. */
  avatarUrl: string | null;
  /** Two-letter monogram for an avatar placeholder. */
  initials: string;
}

/** One membership from the token's `orgs` claim: the org and the coarse role in it. */
export interface IamOrgRef {
  org: string;
  role?: string;
}

/** Decoded JWT payload, or an empty object for an absent/opaque/broken token. */
export function claimsOf(accessToken: string | null | undefined): Record<string, unknown> {
  if (!accessToken) return {};
  try {
    const part = accessToken.split(".")[1];
    if (!part) return {};
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

/**
 * The membership set a token grants: org names in claim order (home first) and the
 * coarse role per org. Falls back to the primary org in `owner`/`sub` for a token
 * minted before the `orgs` claim existed.
 */
export function membershipsFromToken(accessToken: string | null | undefined): {
  organizations: string[];
  roles: Record<string, string>;
} {
  const claims = claimsOf(accessToken);
  const claimed = Array.isArray(claims.orgs) ? (claims.orgs as IamOrgRef[]) : [];

  const organizations: string[] = [];
  const roles: Record<string, string> = {};
  for (const ref of claimed) {
    if (!ref || typeof ref.org !== "string" || ref.org === "") continue;
    if (!organizations.includes(ref.org)) organizations.push(ref.org);
    if (typeof ref.role === "string" && ref.role !== "") roles[ref.org] = ref.role;
  }

  if (organizations.length === 0) {
    const sub = typeof claims.sub === "string" ? claims.sub : "";
    const owner = typeof claims.owner === "string" ? claims.owner : "";
    const primary = owner || (sub.includes("/") ? sub.split("/")[0] : "");
    if (primary) organizations.push(primary);
  }

  return { organizations, roles };
}

/**
 * Resolve the identity to SHOW from userinfo + token claims.
 *
 * Apps that read a single claim render a raw uuid the moment it is absent
 * (hanzo.app did exactly that). This walks every name IAM may carry and falls back
 * only to the email's local part — never to an id.
 */
/**
 * Does this value look like an identifier rather than a name?
 *
 * Reading one claim is not enough: hanzo.app rendered
 * `e7d7fda0-4c53-4508-9d35-7ec892b7e5d7` because its user object carries the uuid
 * in `name`. A name is what a person answers to, so anything id-SHAPED is refused
 * whatever key it arrived under.
 */
export function looksLikeId(value: string): boolean {
  const v = value.trim();
  const bare = v.includes("/") ? v.slice(v.lastIndexOf("/") + 1) : v;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bare)) return true;
  if (/^[0-9a-f]{24,}$/i.test(bare)) return true; // mongo/hex ids
  if (/^[0-9]{10,}$/.test(bare)) return true; // snowflake-ish
  // A single token with no letters-in-words shape and a long random tail.
  return /^[a-z0-9]{20,}$/i.test(bare) && !/[aeiou]{1}/i.test(bare.slice(0, 6));
}

export function resolveIdentity(
  user: Record<string, unknown> | null | undefined,
  claims: Record<string, unknown>,
): IamIdentity | null {
  const u = user ?? {};
  const pick = (...keys: string[]): string | null => {
    for (const k of keys) {
      const v = u[k] ?? claims[k];
      if (typeof v === "string" && v.trim() !== "") return v.trim();
    }
    return null;
  };
  /** Same walk, but an id-shaped value is not a name. */
  const pickName = (...keys: string[]): string | null => {
    for (const k of keys) {
      const v = u[k] ?? claims[k];
      if (typeof v === "string" && v.trim() !== "" && !looksLikeId(v)) return v.trim();
    }
    return null;
  };

  const email = pick("email");
  const name =
    pickName("displayName", "name", "preferred_username", "given_name", "firstName") ??
    (email ? email.split("@")[0] : null);
  if (name == null) return null;

  const initials =
    name
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || name.slice(0, 2).toUpperCase();

  return { name, email, avatarUrl: pick("avatar", "picture", "avatarUrl"), initials };
}


/** Colour theme, per user: an explicit choice or "follow the OS". */
export type ThemeMode = "light" | "dark" | "system";

const THEME_KEY = "hanzo_iam_theme";

/** Where a user's theme choice is remembered — per identity, so two accounts on one browser don't fight. */
export function themeStorageKey(subject?: string | null): string {
  return subject ? `${THEME_KEY}:${subject}` : THEME_KEY;
}

/** The stored choice, or `system` when nothing was ever chosen. */
export function readThemeMode(subject?: string | null): ThemeMode {
  try {
    const v = localStorage.getItem(themeStorageKey(subject));
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}

/** `system` resolved against the OS right now. */
export function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode !== "system") return mode;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "dark";
  }
}

/**
 * Apply a theme the way every Hanzo surface reads it: `data-theme` on <html> plus
 * the `dark` class Tailwind keys off, so one control drives both conventions.
 */
export function applyTheme(mode: ThemeMode): "light" | "dark" {
  const resolved = resolveTheme(mode);
  try {
    const root = document.documentElement;
    root.setAttribute("data-theme", resolved);
    root.classList.toggle("dark", resolved === "dark");
    root.style.colorScheme = resolved;
  } catch {
    /* no DOM (SSR) — the caller re-applies on mount */
  }
  return resolved;
}
